import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './CompanionConfigVersionList.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useParams, Link } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import CompanionConfigVersionListTable from './CompanionConfigVersionListTable';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import SearchBar from '../SearchBar/SearchBar';
import ModelConfigurationContentTemplate from '../ModelTab/ModelConfigurationContentTemplate';
import Pagination from '../Pagination/Pagination';
import { initialCompanionConfigVersionState } from '../../reducers/initialState';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import { getModelCategoryPermission } from '../../utils';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import Cookies from 'js-cookie';

// BCC-17: read-only companion app config version history tab. Mirrors
// ModelFirmwareVersionList.jsx's structure (title/count, breadcrumb, search
// bar, table frame) in every state - the table shell itself never
// disappears, exactly like firmware's screen. "No records found" is left
// blank, same as ModelFirmwareVersionListTable.jsx - only a genuine load
// failure gets a message + retry, since that's the case that must never be
// mistaken for "no versions" (an admin who misreads that could re-upload a
// duplicate version). The search bar filters client-side by Name (the list
// API has no Filter param of its own).
// BCC-18: "+ ADD NEW" entry point to the add form, gated identically to
// firmware's list (COMPANIONCONFIGVERSIONADD widget + model-category owner).
const CompanionConfigVersionList = ({
  getCompanionConfigVersions,
  deleteCompanionConfigVersion,
  companionConfigVersionManager = {
    ...initialCompanionConfigVersionState.companionConfigVersionManager
  },
  intl,
  userProfileWidgets,
  currentTimezone,
  userModelCategories,
  setCurrentPage,
  resetCompanionConfigVersionStatus
}) => {
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  let { ModelCategoryId, ModelId } = useParams();
  // useParams() always returns strings - send ModelId as a number so it
  // matches the type the backend's DB-sourced comparisons expect (a raw
  // string here caused the delete "ownership" check to fail every time via
  // a strict !== against a numeric DB value).
  ModelId = parseInt(ModelId, 10);
  let offset = companionConfigVersionManager.offset || 0;
  let limit = companionConfigVersionManager.limit || 1000;

  const [isFilter, setIsFilter] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [confirmDeleteActive, setConfirmDeleteActive] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(0);

  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };

  const loadVersions = () => {
    if (ModelId) {
      getCompanionConfigVersions(ModelId, offset, limit);
    }
  };

  useEffect(() => {
    loadVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCompanionConfigVersions, ModelId, offset, limit]);

  // A successful delete leaves isOpSuccessful: true / showBanner: false
  // sitting in the shared companionConfigVersionManager slice once this
  // screen's own delete Banner auto-hides (setCompanionConfigVersionStatus
  // hardcodes isOpSuccessful: true and only ever toggles showBanner - see
  // that action's comment). CompanionConfigVersion.jsx (the add/edit form)
  // reads those same two fields to auto-<Navigate> away right after ITS OWN
  // save succeeds - so without this, the first mount of that form after a
  // delete here saw the leftover flags and immediately bounced straight
  // back to this list before rendering, and only the *second* Add New/Edit
  // click (after this cleanup had already run once) got through. Resetting
  // on unmount - i.e. exactly when navigating away, to the form or anywhere
  // else - guarantees the form never sees a stale success from a delete it
  // had nothing to do with, regardless of the delete banner's own 2500ms
  // auto-hide timing.
  useEffect(() => {
    return () => {
      resetCompanionConfigVersionStatus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // BCC-18: delete (draft only - the backend rejects a Published row).
  // Same ConfirmDialog-driven pattern as ModelSetpointsList.jsx.
  const onRemoveClick = modelCompanionConfigVersionId => {
    setDeleteTargetId(modelCompanionConfigVersionId);
    setConfirmDeleteActive(true);
  };

  const handleDeleteClose = () => {
    setConfirmDeleteActive(false);
    setDeleteTargetId(0);
  };

  const handleDeleteConfirm = () => {
    let deleteData = {
      ModelId: ModelId,
      ModelCompanionConfigVersionId: deleteTargetId,
      UpdatedBy: Cookies.get(`userid-${envName}`) || 0
    };
    // deleteCompanionConfigVersion's promise resolves whether the delete
    // succeeded or failed (apiMiddleware.js's .catch swallows the axios
    // rejection after dispatching onFailure) - refetching either way just
    // re-shows the current list, which is harmless.
    deleteCompanionConfigVersion(deleteData, 'COMPANIONCONFIGVERSIONDELETE').then(() => {
      loadVersions();
    });
    handleDeleteClose();
  };

  const searchHandler = value => {
    setIsFilter(value !== '');
    setSearchText(value);
  };

  const filterData = () => {
    let list = companionConfigVersionManager.companionConfigVersions || [];
    if (isFilter) {
      list = list.filter(item => (item.Name || '').toLowerCase().includes(searchText.toLowerCase()));
    }
    return list;
  };

  // Only render a detail line when the backend actually gave us a message
  // code - unlike the 'none' fallback used elsewhere for the success/failure
  // Banner, a blank error detail here should just be omitted, not show the
  // literal fallback text.
  const displayMessageCode = companionConfigVersionManager && companionConfigVersionManager.displayMessageCode;
  const messageText = displayMessageCode ? intl.formatMessage({ id: displayMessageCode, defaultMessage: displayMessageCode }) : '';

  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="model.manageCompanionConfigVersions" defaultMessage="Companion App Config Versions" /> }
  ];

  const dataObj = Object.values(filterData());
  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner);
  const isUpdateAllowed = IsWidgetAccessible(userProfileWidgets, 'COMPANIONCONFIGVERSIONUPDATE');
  const isDeleteAllowed = IsWidgetAccessible(userProfileWidgets, 'COMPANIONCONFIGVERSIONDELETE');

  const dialogTitle = intl.formatMessage({
    id: 'companionConfigVersion.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this companion app config version?'
  });

  return (
    <div className={s.companionConfigVersion}>
      {companionConfigVersionManager.isLoading && <LoadingSpinner />}
      <Banner
        failureText={messageText}
        showBanner={companionConfigVersionManager.showBanner}
        status={companionConfigVersionManager.isOpSuccessful}
        successText={messageText}
      />
      <ConfirmDialog title={dialogTitle} onConfirm={handleDeleteConfirm} onClose={handleDeleteClose} showDialog={confirmDeleteActive} />
      <ModelConfigurationContentTemplate selectedPage="manageCompanionConfigVersions" userProfileWidgets={userProfileWidgets} intl={intl}>
        <div className={s.contentWrapper}>
          <div className={s.componentHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="model.manageCompanionConfigVersions" defaultMessage="Companion App Config Versions" />
              {!companionConfigVersionManager.hasLoadError && (
                <span className={s.circle}>{(companionConfigVersionManager.companionConfigVersions || []).length}</span>
              )}
            </div>
          </div>
          <div className="pb-3">
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <Row className={s.topRow}>
            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                placeHolderTextId="companionConfigVersion.searchPlaceholder"
              />
            </Col>
            <Col sm={6} lg={8}>
              {isModelCategoryOwner ? (
                <Link to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/companion-config-version/add`}>
                  <Button className={s.addbutton} style={WidgetVisibility(userProfileWidgets, 'COMPANIONCONFIGVERSIONADD')}>
                    + <FormattedMessage id="companionConfigVersion.addNew" defaultMessage="ADD NEW" />
                  </Button>
                </Link>
              ) : (
                ''
              )}
            </Col>
          </Row>

          <div>
            <Pagination
              data={dataObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={companionConfigVersionManager.currentPage}>
              <CompanionConfigVersionListTable
                currentTimezone={currentTimezone}
                hasLoadError={companionConfigVersionManager.hasLoadError}
                errorMessageText={messageText}
                onRetry={loadVersions}
                ModelCategoryId={ModelCategoryId}
                ModelId={ModelId}
                isModelCategoryOwner={isModelCategoryOwner}
                UpdateAllowed={isUpdateAllowed}
                DeleteAllowed={isDeleteAllowed}
                onDeleteClick={onRemoveClick}
              />
            </Pagination>
          </div>
        </div>
      </ModelConfigurationContentTemplate>
    </div>
  );
};

CompanionConfigVersionList.propTypes = {
  getCompanionConfigVersions: PropTypes.func.isRequired,
  deleteCompanionConfigVersion: PropTypes.func.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  resetCompanionConfigVersionStatus: PropTypes.func.isRequired,
  currentTimezone: PropTypes.string.isRequired
};

export default injectIntl(CompanionConfigVersionList);
